import React, { useEffect, useState, useCallback } from 'react';
import { MainLayout } from '@/components/Layout';
import { pdv, products, categories, paymentMethods, orders as ordersApi } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Users, Coffee, Utensils, Plus, Minus, 
  ArrowRightLeft, Receipt, CreditCard, Check,
  FileText, Trash2, Lock, Unlock, Play, RotateCcw,
  Percent, XCircle, Split, Search, Clock, MessageSquare, Flame, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { CieloPixDialog, useCieloStatus } from '@/components/cielo/CieloPixDialog';
import api from '@/lib/api';

const TABLE_STATUS = {
  LIVRE: { color: 'bg-emerald-100 border-emerald-400 text-emerald-800', icon: Coffee, label: 'Livre' },
  OCUPADA: { color: 'bg-rose-100 border-rose-400 text-rose-800', icon: Users, label: 'Ocupada' },
  CONTA_PEDIDA: { color: 'bg-amber-100 border-amber-400 text-amber-800', icon: Receipt, label: 'Conta' },
  LIMPEZA: { color: 'bg-slate-100 border-slate-400 text-slate-600', icon: Utensils, label: 'Limpeza' },
};

const canEditOrder = (tableOrderStatus) => {
  return !tableOrderStatus || tableOrderStatus === 'OPEN' || tableOrderStatus === 'BILL_REQUESTED';
};

export function SalaoPage() {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [productsList, setProductsList] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [paymentMethodsList, setPaymentMethodsList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [productSearch, setProductSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [orderItems, setOrderItems] = useState([]);
  const [orderData, setOrderData] = useState(null);
  const [tableOrderStatus, setTableOrderStatus] = useState(null);
  
  // Modals
  const [showOpenTable, setShowOpenTable] = useState(false);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const cielo = useCieloStatus();
  const [cieloPix, setCieloPix] = useState(null);
  const [cieloBalance, setCieloBalance] = useState(null);
  useEffect(() => {
    const oid = selectedTable?.current_order?.id;
    if (!showPayment || !cielo.pix_enabled || !oid) { setCieloBalance(null); return; }
    api.get(`/integrations/cielo-gateway/orders/${oid}/prefill`).then(r => {
      setCieloBalance(r.data.balance);
      if (r.data.paid > 0) setPayments([{ method_id: '', amount: String(r.data.balance) }]);
    }).catch(() => setCieloBalance(null));
  }, [showPayment, cielo.pix_enabled, selectedTable?.current_order?.id]);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);
  const [showSplit, setShowSplit] = useState(false);
  const [showItemNotes, setShowItemNotes] = useState(null);
  
  // Form states
  const [customerName, setCustomerName] = useState('');
  const [peopleCount, setPeopleCount] = useState(1);
  const [targetTableId, setTargetTableId] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [discountType, setDiscountType] = useState('PERCENT');
  const [discountValue, setDiscountValue] = useState('');
  const [splitParts, setSplitParts] = useState(2);
  const [itemNotes, setItemNotes] = useState('');
  
  // Multi-payment
  const [payments, setPayments] = useState([{ method_id: '', amount: '' }]);

  const loadData = useCallback(async () => {
    try {
      const [tablesRes, productsRes, categoriesRes, methodsRes] = await Promise.all([
        pdv.tables.list(),
        products.list(),
        categories.list(),
        paymentMethods.list()
      ]);
      setTables(tablesRes.data);
      setProductsList(productsRes.data);
      setCategoriesList(categoriesRes.data);
      
      if (!methodsRes.data || methodsRes.data.length === 0) {
        try {
          await paymentMethods.initDefaults();
          const newMethodsRes = await paymentMethods.list();
          setPaymentMethodsList(newMethodsRes.data);
        } catch (initError) {
          setPaymentMethodsList([]);
        }
      } else {
        setPaymentMethodsList(methodsRes.data);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const loadOrderItems = async (orderId) => {
    try {
      const res = await ordersApi.get(orderId);
      const items = (res.data.items || []).filter(i => i.status !== 'CANCELADO');
      setOrderItems(items);
      setOrderData(res.data);
    } catch (error) {
      console.error('Error loading order:', error);
    }
  };

  const handleTableClick = async (table) => {
    setSelectedTable(table);
    setTableOrderStatus(table.current_order?.table_order_status || null);
    
    if (table.status === 'LIVRE') {
      setShowOpenTable(true);
    } else if (table.status === 'OCUPADA' || table.status === 'CONTA_PEDIDA') {
      if (table.current_order?.id) {
        await loadOrderItems(table.current_order.id);
        setTableOrderStatus(table.current_order.table_order_status || 'OPEN');
      }
      setShowOrderDetail(true);
    } else if (table.status === 'LIMPEZA') {
      await handleFreeTable(table.id);
    }
  };

  const handleOpenTable = async () => {
    if (!selectedTable) return;
    try {
      await pdv.tables.open(selectedTable.id, {
        table_id: selectedTable.id,
        customer_name: customerName,
        people_count: peopleCount
      });
      toast.success(`Mesa ${selectedTable.name} aberta!`);
      setShowOpenTable(false);
      setCustomerName('');
      setPeopleCount(1);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao abrir mesa');
    }
  };

  const handleResetAllTables = async () => {
    if (!window.confirm('Resetar TODAS as mesas? Pedidos nao pagos serao cancelados.')) return;
    try {
      const res = await pdv.tables.resetAll();
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error('Erro ao resetar mesas');
    }
  };

  const handleFreeTable = async (tableId) => {
    try {
      await pdv.tables.free(tableId);
      toast.success('Mesa liberada!');
      loadData();
    } catch (error) {
      toast.error('Erro ao liberar mesa');
    }
  };

  const [sendingKitchen, setSendingKitchen] = useState(false);

  const hasPendingItems = orderItems.some(i => !i.status || i.status === 'PENDENTE');

  const handleSendToKitchen = async () => {
    if (!selectedTable?.current_order?.id) return;
    setSendingKitchen(true);
    try {
      const res = await pdv.orders.sendToKitchen(selectedTable.current_order.id);
      if (res.data?.success) {
        toast.success(res.data.message || 'Enviado para cozinha!');
        // Atualizar status dos itens localmente
        setOrderItems(prev => prev.map(item => 
          (!item.status || item.status === 'PENDENTE') 
            ? { ...item, status: 'ENVIADO' } 
            : item
        ));
        await loadOrderItems(selectedTable.current_order.id);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao enviar para cozinha';
      toast.error(msg);
    } finally {
      setSendingKitchen(false);
    }
  };

  const handleRequestBill = async () => {
    if (!selectedTable) return;
    try {
      await pdv.tables.requestBill(selectedTable.id);
      toast.success('Conta solicitada!');
      setTableOrderStatus('BILL_REQUESTED');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro');
    }
  };

  const handleCloseBill = async () => {
    if (!selectedTable) return;
    try {
      await pdv.tables.closeBill(selectedTable.id);
      toast.success('Conta fechada! Itens travados.');
      setTableOrderStatus('BILL_CLOSED');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro');
    }
  };

  const handleResume = async () => {
    if (!selectedTable) return;
    try {
      await pdv.tables.resume(selectedTable.id);
      toast.success('Mesa voltou para consumo!');
      setTableOrderStatus('OPEN');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro');
    }
  };

  const handleReopenBill = async () => {
    if (!selectedTable) return;
    try {
      await pdv.tables.reopenBill(selectedTable.id);
      toast.success('Conta reaberta!');
      setTableOrderStatus('BILL_REQUESTED');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro');
    }
  };

  const handleTransfer = async () => {
    if (!selectedTable || !targetTableId) return;
    try {
      await pdv.tables.transfer({ from_table_id: selectedTable.id, to_table_id: targetTableId });
      toast.success('Mesa transferida!');
      setShowTransfer(false);
      setShowOrderDetail(false);
      setTargetTableId('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro');
    }
  };

  // === ITEM CRUD ===
  const handleAddItem = async (product) => {
    if (!selectedTable?.current_order?.id) return;
    if (!canEditOrder(tableOrderStatus)) {
      toast.error('Conta fechada.');
      return;
    }
    try {
      const payload = { product_id: product.id, quantity: 1 };
      if (itemNotes) {
        payload.notes = itemNotes;
        setItemNotes('');
        setShowItemNotes(null);
      }
      await pdv.orders.addItem(selectedTable.current_order.id, payload);
      toast.success(`${product.name} adicionado!`);
      await loadOrderItems(selectedTable.current_order.id);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro');
    }
  };

  const handleUpdateQuantity = async (itemId, newQty) => {
    if (!selectedTable?.current_order?.id) return;
    if (!canEditOrder(tableOrderStatus)) return;
    try {
      if (newQty <= 0) {
        await pdv.orders.removeItem(selectedTable.current_order.id, itemId);
        toast.success('Item removido');
      } else {
        await pdv.orders.updateItemQty(selectedTable.current_order.id, itemId, newQty);
      }
      await loadOrderItems(selectedTable.current_order.id);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro');
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (!selectedTable?.current_order?.id || !canEditOrder(tableOrderStatus)) return;
    try {
      await pdv.orders.removeItem(selectedTable.current_order.id, itemId);
      toast.success('Item removido');
      await loadOrderItems(selectedTable.current_order.id);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro');
    }
  };

  // === TAXA DE SERVICO ===
  const handleToggleServiceFee = async () => {
    if (!selectedTable?.current_order?.id) return;
    try {
      const res = await pdv.orders.toggleServiceFee(selectedTable.current_order.id);
      toast.success(res.data.service_fee_enabled ? 'Taxa de servico ativada' : 'Taxa de servico removida');
      await loadOrderItems(selectedTable.current_order.id);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro');
    }
  };

  // === DESCONTO ===
  const handleApplyDiscount = async () => {
    if (!selectedTable?.current_order?.id || !discountValue) return;
    try {
      await pdv.orders.applyDiscount(selectedTable.current_order.id, {
        discount_type: discountType,
        value: parseFloat(discountValue)
      });
      toast.success('Desconto aplicado!');
      setShowDiscount(false);
      setDiscountValue('');
      await loadOrderItems(selectedTable.current_order.id);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro');
    }
  };

  const handleRemoveDiscount = async () => {
    if (!selectedTable?.current_order?.id) return;
    try {
      await pdv.orders.removeDiscount(selectedTable.current_order.id);
      toast.success('Desconto removido');
      await loadOrderItems(selectedTable.current_order.id);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro');
    }
  };

  // === CANCELAMENTO ===
  const handleCancelOrder = async () => {
    if (!selectedTable?.current_order?.id || !cancelReason.trim()) {
      toast.error('Informe o motivo do cancelamento');
      return;
    }
    try {
      await pdv.orders.cancel(selectedTable.current_order.id, { reason: cancelReason });
      toast.success('Pedido cancelado');
      setShowCancel(false);
      setShowOrderDetail(false);
      setCancelReason('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro');
    }
  };

  // === SPLIT BILL ===
  const handleSplitBill = async () => {
    if (!selectedTable?.current_order?.id) return;
    try {
      const res = await pdv.orders.splitBill(selectedTable.current_order.id, {
        order_id: selectedTable.current_order.id,
        split_type: "IGUAL",
        parts: splitParts
      });
      const partsInfo = res.data.parts.map(p => `Parte ${p.part}: ${formatCurrency(p.amount)}`).join('\n');
      toast.success(`Conta dividida em ${splitParts} partes!`);
      setShowSplit(false);
      alert(`Divisao da conta:\n\n${partsInfo}\n\nTotal: ${formatCurrency(res.data.total)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro');
    }
  };

  // === MULTIPLOS PAGAMENTOS ===
  const addPaymentLine = () => {
    setPayments([...payments, { method_id: '', amount: '' }]);
  };

  const removePaymentLine = (idx) => {
    if (payments.length <= 1) return;
    setPayments(payments.filter((_, i) => i !== idx));
  };

  const updatePaymentLine = (idx, field, value) => {
    const updated = [...payments];
    updated[idx][field] = value;
    setPayments(updated);
  };

  const totalPayments = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const orderTotal = orderData?.total || selectedTable?.current_order?.total || 0;
  const orderSubtotal = orderData?.subtotal || 0;
  const orderServiceFee = orderData?.service_fee || 0;
  const orderDiscount = orderData?.discount || 0;

  const handleMultiPayment = async () => {
    if (!selectedTable?.current_order?.id) return;
    const validPayments = payments.filter(p => p.method_id && parseFloat(p.amount) > 0);
    if (validPayments.length === 0) {
      toast.error('Adicione pelo menos um pagamento');
      return;
    }
    const total = validPayments.reduce((s, p) => s + parseFloat(p.amount), 0);
    const amountDue = cieloBalance ?? orderTotal;
    if (Math.abs(total - amountDue) > 0.01) {
      toast.error(`Soma dos pagamentos (${formatCurrency(total)}) diferente do total (${formatCurrency(amountDue)})`);
      return;
    }
    const cieloLine = validPayments.find(p => p.method_id === '__cielo_pix__');
    const regularPayments = validPayments.filter(p => p.method_id !== '__cielo_pix__');
    if (cieloLine) {
      setShowPayment(false);
      try {
        for (const p of regularPayments) {
          await pdv.orders.payment(selectedTable.current_order.id, {
            order_id: selectedTable.current_order.id, payment_method_id: p.method_id, amount: parseFloat(p.amount),
            received_amount: null, idempotency_key: crypto.randomUUID()
          });
        }
        if (regularPayments.length) toast.success('Pagamentos registrados. Gere o PIX Cielo do restante.');
        setPayments([{ method_id: '', amount: '' }]);
        setCieloPix({ orderId: selectedTable.current_order.id, amount: parseFloat(cieloLine.amount) });
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erro no pagamento');
      }
      loadData();
      return;
    }

    setShowPayment(false);
    setShowOrderDetail(false);
    
    try {
      // Processar cada pagamento sequencialmente
      for (const p of validPayments) {
        const idempotencyKey = crypto.randomUUID(); // Chave única para evitar duplicação
        await pdv.orders.payment(selectedTable.current_order.id, {
          order_id: selectedTable.current_order.id,
          payment_method_id: p.method_id,
          amount: parseFloat(p.amount),
          received_amount: null,
          idempotency_key: idempotencyKey
        });
      }
      toast.success('Pagamento realizado!');
      setPayments([{ method_id: '', amount: '' }]);
      setSelectedTable(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro no pagamento');
      loadData();
    }
  };

  // Filtros de produto
  const filteredProducts = productsList
    .filter(p => p.is_active !== false)
    .filter(p => selectedCategory === 'all' || p.category_id === selectedCategory)
    .filter(p => !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase()));

  const freeTables = tables.filter(t => t.status === 'LIVRE');
  const isEditable = canEditOrder(tableOrderStatus);
  const isBillClosed = tableOrderStatus === 'BILL_CLOSED';

  // Tempo de ocupacao
  const getOccupiedTime = (table) => {
    if (!table.occupied_at) return null;
    const diff = Date.now() - new Date(table.occupied_at).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}min`;
    return `${Math.floor(mins/60)}h${mins%60}min`;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-500">Carregando...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 page-enter" data-testid="salao-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Barlow Condensed' }}>
              Salao
            </h1>
            <p className="text-slate-600">Gestao de mesas e comandas</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleResetAllTables}
              className="border-red-300 text-red-600 hover:bg-red-50" data-testid="reset-all-tables-btn">
              <RotateCcw size={16} className="mr-2" /> Resetar Mesas
            </Button>
            <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
              <span className="text-sm font-medium text-emerald-700">{freeTables.length} livres</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-rose-50 rounded-lg border border-rose-200">
              <div className="w-3 h-3 rounded-full bg-rose-500"></div>
              <span className="text-sm font-medium text-rose-700">{tables.length - freeTables.length} ocupadas</span>
            </div>
          </div>
        </div>

        {/* Mapa de Mesas */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Utensils size={20} className="text-[#FF6B6B]" /> Mapa de Mesas
          </h2>
          
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
            {tables.map((table) => {
              const statusConfig = TABLE_STATUS[table.status] || TABLE_STATUS.LIVRE;
              const StatusIcon = statusConfig.icon;
              const isLocked = table.current_order?.table_order_status === 'BILL_CLOSED';
              const occupiedTime = getOccupiedTime(table);
              
              return (
                <button key={table.id} onClick={() => handleTableClick(table)}
                  data-testid={`table-${table.id}`}
                  className={cn("relative p-4 rounded-xl border-2 transition-all hover:scale-105 hover:shadow-md", statusConfig.color)}>
                  <div className="flex flex-col items-center gap-1">
                    <StatusIcon size={24} />
                    <span className="font-bold text-lg">{table.name}</span>
                    <span className="text-xs font-medium">{statusConfig.label}</span>
                    {table.current_order && (
                      <span className="text-sm font-bold">{formatCurrency(table.current_order.total)}</span>
                    )}
                    {occupiedTime && (
                      <span className="text-xs opacity-70 flex items-center gap-1">
                        <Clock size={10} /> {occupiedTime}
                      </span>
                    )}
                  </div>
                  {table.capacity && (
                    <span className="absolute top-1 right-1 text-xs bg-white/80 px-1.5 py-0.5 rounded">{table.capacity}p</span>
                  )}
                  {isLocked && (
                    <span className="absolute top-1 left-1"><Lock size={14} className="text-red-600" /></span>
                  )}
                </button>
              );
            })}
          </div>
          
          {tables.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Utensils size={48} className="mx-auto mb-4 opacity-30" />
              <p>Nenhuma mesa cadastrada</p>
            </div>
          )}
        </div>

        {/* Legenda */}
        <div className="flex flex-wrap gap-4 justify-center">
          {Object.entries(TABLE_STATUS).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <div key={key} className="flex items-center gap-2 text-sm">
                <div className={cn("w-6 h-6 rounded flex items-center justify-center border", config.color)}>
                  <Icon size={14} />
                </div>
                <span className="text-slate-600">{config.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ========== MODAL: Abrir Mesa ========== */}
      <Dialog open={showOpenTable} onOpenChange={setShowOpenTable}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coffee className="text-[#FF6B6B]" /> Abrir {selectedTable?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome do Cliente (opcional)</Label>
              <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Nome do cliente" className="mt-1" data-testid="customer-name-input" />
            </div>
            <div>
              <Label>Numero de Pessoas</Label>
              <div className="flex items-center gap-3 mt-1">
                <Button variant="outline" size="icon" onClick={() => setPeopleCount(Math.max(1, peopleCount - 1))}><Minus size={16} /></Button>
                <span className="text-xl font-bold w-12 text-center">{peopleCount}</span>
                <Button variant="outline" size="icon" onClick={() => setPeopleCount(peopleCount + 1)}><Plus size={16} /></Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOpenTable(false)}>Cancelar</Button>
            <Button onClick={handleOpenTable} className="bg-[#FF6B6B] hover:bg-[#FF5252]" data-testid="confirm-open-table">Abrir Mesa</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== MODAL: Detalhes do Pedido ========== */}
      <Dialog open={showOrderDetail} onOpenChange={setShowOrderDetail}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="text-[#FF6B6B]" /> {selectedTable?.name}
              {isBillClosed && <span className="ml-2 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full flex items-center gap-1"><Lock size={12} /> Fechada</span>}
              {tableOrderStatus === 'BILL_REQUESTED' && <span className="ml-2 px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">Conta Solicitada</span>}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="items" className="flex-1">
            <TabsList className="bg-slate-100">
              <TabsTrigger value="items">Itens do Pedido</TabsTrigger>
              <TabsTrigger value="add" disabled={!isEditable}>
                Adicionar {!isEditable && <Lock size={12} className="ml-1" />}
              </TabsTrigger>
            </TabsList>
            
            {/* === ABA ITENS === */}
            <TabsContent value="items" className="max-h-[45vh] overflow-y-auto">
              <div className="space-y-2">
                {orderItems.length > 0 ? orderItems.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{item.product_name}</p>
                        {item.status === 'ENVIADO' && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">Enviado</span>}
                        {item.status === 'PRONTO' && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Pronto</span>}
                        {(!item.status || item.status === 'PENDENTE') && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Pendente</span>}
                      </div>
                      <p className="text-sm text-slate-500">{formatCurrency(item.unit_price)} un.</p>
                      {item.notes && <p className="text-xs text-amber-600 mt-1">Obs: {item.notes}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      {isEditable && (
                        <div className="flex items-center gap-1 bg-white border rounded-lg px-1">
                          <button onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                            className="p-1 hover:bg-slate-100 rounded" data-testid={`qty-minus-${item.id}`}>
                            <Minus size={14} />
                          </button>
                          <span className="w-8 text-center font-bold text-sm">{item.quantity}</span>
                          <button onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                            className="p-1 hover:bg-slate-100 rounded" data-testid={`qty-plus-${item.id}`}>
                            <Plus size={14} />
                          </button>
                        </div>
                      )}
                      {!isEditable && <span className="text-sm text-slate-500">{item.quantity}x</span>}
                      <span className="font-bold w-20 text-right">{formatCurrency(item.total_price)}</span>
                      {isEditable && (
                        <button onClick={() => handleRemoveItem(item.id)}
                          className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`remove-item-${item.id}`}>
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                )) : (
                  <p className="text-center text-slate-500 py-8">Nenhum item no pedido</p>
                )}
              </div>
              
              {/* Resumo financeiro */}
              <div className="mt-4 p-4 bg-slate-100 rounded-lg space-y-2">
                <div className="flex justify-between text-sm text-slate-600">
                  <span>Subtotal</span>
                  <span>{formatCurrency(orderSubtotal)}</span>
                </div>
                {orderServiceFee > 0 && (
                  <div className="flex justify-between text-sm text-slate-600">
                    <span className="flex items-center gap-1">
                      Taxa de Servico (10%)
                      {isEditable && (
                        <button onClick={handleToggleServiceFee} className="text-xs text-red-500 hover:text-red-700 underline" data-testid="toggle-service-fee">
                          remover
                        </button>
                      )}
                    </span>
                    <span>{formatCurrency(orderServiceFee)}</span>
                  </div>
                )}
                {orderServiceFee === 0 && isEditable && orderSubtotal > 0 && (
                  <div className="flex justify-between text-sm">
                    <button onClick={handleToggleServiceFee} className="text-blue-600 hover:text-blue-800 underline text-xs" data-testid="add-service-fee">
                      + Adicionar taxa de servico (10%)
                    </button>
                  </div>
                )}
                {orderDiscount > 0 && (
                  <div className="flex justify-between text-sm text-emerald-600">
                    <span className="flex items-center gap-1">
                      Desconto
                      {isEditable && (
                        <button onClick={handleRemoveDiscount} className="text-xs text-red-500 hover:text-red-700 underline">remover</button>
                      )}
                    </span>
                    <span>-{formatCurrency(orderDiscount)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-2 border-t border-slate-300">
                  <span className="text-lg font-semibold">Total</span>
                  <span className="text-2xl font-bold text-[#FF6B6B]">{formatCurrency(orderTotal)}</span>
                </div>
              </div>
            </TabsContent>
            
            {/* === ABA ADICIONAR ITENS === */}
            <TabsContent value="add" className="max-h-[45vh] overflow-y-auto">
              {!isEditable ? (
                <div className="text-center py-8 text-slate-500">
                  <Lock size={48} className="mx-auto mb-4 opacity-30" />
                  <p>Conta fechada. Reabra para editar.</p>
                </div>
              ) : (
                <>
                  {/* Busca */}
                  <div className="relative mb-3">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input placeholder="Buscar produto..." value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      className="pl-9" data-testid="product-search" />
                  </div>
                  {/* Categorias */}
                  <div className="flex gap-2 flex-wrap mb-4">
                    <button onClick={() => setSelectedCategory('all')}
                      className={cn("px-3 py-1.5 rounded-full text-sm font-medium transition-colors",
                        selectedCategory === 'all' ? 'bg-[#FF6B6B] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200')}>
                      Todos
                    </button>
                    {categoriesList.map((cat) => (
                      <button key={cat.id} onClick={() => setSelectedCategory(cat.id)}
                        className={cn("px-3 py-1.5 rounded-full text-sm font-medium transition-colors",
                          selectedCategory === cat.id ? 'bg-[#FF6B6B] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200')}>
                        {cat.name}
                      </button>
                    ))}
                  </div>
                  {/* Grid de Produtos */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {filteredProducts.map((product) => (
                      <div key={product.id} className="p-3 bg-white border border-slate-200 rounded-lg hover:border-[#FF6B6B] hover:shadow-sm transition-all">
                        <button onClick={() => handleAddItem(product)} className="w-full text-left" data-testid={`add-product-${product.id}`}>
                          <p className="font-medium text-sm text-slate-900 truncate">{product.name}</p>
                          <p className="text-[#FF6B6B] font-bold">{formatCurrency(product.price)}</p>
                        </button>
                        <button onClick={() => { setShowItemNotes(product); setItemNotes(''); }}
                          className="mt-1 text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1" data-testid={`notes-btn-${product.id}`}>
                          <MessageSquare size={10} /> + obs
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
          
          {/* Botoes de Acao */}
          <DialogFooter className="flex-wrap gap-2 border-t pt-4">
            {tableOrderStatus !== 'PAID' && (
              <Button variant="outline" onClick={() => setShowTransfer(true)} className="flex items-center gap-1" data-testid="transfer-table-btn">
                <ArrowRightLeft size={14} /> Transferir
              </Button>
            )}
            
            {isEditable && orderSubtotal > 0 && (
              <Button variant="outline" onClick={() => setShowDiscount(true)} className="flex items-center gap-1" data-testid="discount-btn">
                <Percent size={14} /> Desconto
              </Button>
            )}

            {(tableOrderStatus === 'OPEN' || !tableOrderStatus) && (
              <>
                <Button variant="outline" onClick={() => setShowCancel(true)} className="flex items-center gap-1 border-red-300 text-red-600" data-testid="cancel-order-btn">
                  <XCircle size={14} /> Cancelar
                </Button>
                <Button variant="outline" onClick={handleRequestBill} className="flex items-center gap-1" data-testid="request-bill-btn">
                  <FileText size={14} /> Pedir Conta
                </Button>
                <Button variant="outline" onClick={handleCloseBill} className="flex items-center gap-1 border-orange-300 text-orange-600" data-testid="close-bill-btn">
                  <Lock size={14} /> Fechar Mesa
                </Button>
              </>
            )}
            
            {tableOrderStatus === 'BILL_REQUESTED' && (
              <>
                <Button variant="outline" onClick={handleResume} className="flex items-center gap-1" data-testid="resume-btn">
                  <Play size={14} /> Voltar Consumo
                </Button>
                <Button variant="outline" onClick={() => setShowSplit(true)} className="flex items-center gap-1" data-testid="split-btn">
                  <Split size={14} /> Dividir Conta
                </Button>
                <Button variant="outline" onClick={handleCloseBill} className="flex items-center gap-1 border-orange-300 text-orange-600" data-testid="close-bill-btn-req">
                  <Lock size={14} /> Fechar
                </Button>
              </>
            )}
            
            {tableOrderStatus === 'BILL_CLOSED' && (
              <Button variant="outline" onClick={handleReopenBill} className="flex items-center gap-1 border-blue-300 text-blue-600" data-testid="reopen-bill-btn">
                <Unlock size={14} /> Reabrir
              </Button>
            )}
            
            {(tableOrderStatus === 'BILL_REQUESTED' || tableOrderStatus === 'BILL_CLOSED') && (
              <Button onClick={() => { setPayments([{ method_id: '', amount: String(orderTotal) }]); setShowPayment(true); }}
                className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-1" data-testid="open-payment-modal">
                <CreditCard size={14} /> Pagar
              </Button>
            )}
            
            {(tableOrderStatus === 'OPEN' || !tableOrderStatus) && hasPendingItems && (
              <Button 
                onClick={handleSendToKitchen} 
                disabled={sendingKitchen}
                className="bg-orange-500 hover:bg-orange-600 flex items-center gap-1 text-white" 
                data-testid="send-kitchen-btn">
                {sendingKitchen ? (
                  <><Loader2 size={14} className="animate-spin" /> Enviando...</>
                ) : (
                  <><Flame size={14} /> Enviar para Cozinha</>
                )}
              </Button>
            )}
            {(tableOrderStatus === 'OPEN' || !tableOrderStatus) && orderSubtotal > 0 && !hasPendingItems && (
              <Button onClick={() => { setPayments([{ method_id: '', amount: String(orderTotal) }]); setShowPayment(true); }}
                className="bg-[#FF6B6B] hover:bg-[#FF5252] flex items-center gap-1" data-testid="quick-payment-btn">
                <CreditCard size={14} /> Fechar Conta
              </Button>
            )}
            {(tableOrderStatus === 'OPEN' || !tableOrderStatus) && orderSubtotal > 0 && hasPendingItems && (
              <Button variant="outline" onClick={() => { setPayments([{ method_id: '', amount: String(orderTotal) }]); setShowPayment(true); }}
                className="flex items-center gap-1 text-slate-400 border-slate-200" data-testid="quick-payment-btn-disabled"
                disabled title="Envie itens pendentes para cozinha antes de fechar">
                <CreditCard size={14} /> Fechar Conta
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== MODAL: Transferir ========== */}
      <Dialog open={showTransfer} onOpenChange={setShowTransfer}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle><ArrowRightLeft className="inline mr-2 text-[#FF6B6B]" size={20} />Transferir Mesa</DialogTitle></DialogHeader>
          <div className="py-4">
            <Label>Mesa de Destino</Label>
            <Select value={targetTableId} onValueChange={setTargetTableId}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione a mesa" /></SelectTrigger>
              <SelectContent>
                {freeTables.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransfer(false)}>Cancelar</Button>
            <Button onClick={handleTransfer} className="bg-[#FF6B6B] hover:bg-[#FF5252]" disabled={!targetTableId}>Transferir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== MODAL: Pagamento Multi ========== */}
      <Dialog open={showPayment} onOpenChange={setShowPayment}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader><DialogTitle><CreditCard className="inline mr-2 text-[#FF6B6B]" size={20} />Pagamento - {selectedTable?.name}</DialogTitle></DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg text-center">
              <p className="text-sm text-slate-500 mb-1">Total a Pagar</p>
              <p className="text-3xl font-bold text-[#FF6B6B]">{formatCurrency(cieloBalance ?? orderTotal)}</p>
              {cieloBalance != null && Math.abs(cieloBalance - orderTotal) > 0.01 && <p className="text-xs text-slate-500" data-testid="payment-balance-note">Total {formatCurrency(orderTotal)} · já recebido {formatCurrency(orderTotal - cieloBalance)}</p>}
            </div>
            
            <div className="space-y-3">
              <Label>Formas de Pagamento</Label>
              {payments.map((p, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Select value={p.method_id} onValueChange={(v) => updatePaymentLine(idx, 'method_id', v)}>
                    <SelectTrigger className="flex-1" data-testid={`payment-method-${idx}`}>
                      <SelectValue placeholder="Metodo" />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentMethodsList.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                      {cielo.pix_enabled && <SelectItem value="__cielo_pix__" data-testid={`payment-method-cielo-pix-${idx}`}>PIX Cielo (QR Code)</SelectItem>}
                    </SelectContent>
                  </Select>
                  <Input type="number" step="0.01" placeholder="Valor" value={p.amount}
                    onChange={(e) => updatePaymentLine(idx, 'amount', e.target.value)}
                    className="w-32" data-testid={`payment-amount-${idx}`} />
                  {payments.length > 1 && (
                    <button onClick={() => removePaymentLine(idx)} className="p-1 text-red-400 hover:text-red-600">
                      <XCircle size={18} />
                    </button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addPaymentLine} className="w-full" data-testid="add-payment-line">
                <Plus size={14} className="mr-1" /> Adicionar forma de pagamento
              </Button>
            </div>
            
            {payments.length > 1 && (
              <div className={cn("text-sm font-medium text-center p-2 rounded",
                Math.abs(totalPayments - orderTotal) < 0.01 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700")}>
                Soma: {formatCurrency(totalPayments)} / Total: {formatCurrency(orderTotal)}
                {Math.abs(totalPayments - orderTotal) < 0.01 && ' OK'}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayment(false)}>Cancelar</Button>
            <Button onClick={handleMultiPayment}
              className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2"
              disabled={!payments.some(p => p.method_id && parseFloat(p.amount) > 0)}
              data-testid="confirm-payment">
              <Check size={16} /> Confirmar Pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CieloPixDialog open={!!cieloPix} orderId={cieloPix?.orderId} initialAmount={cieloPix?.amount}
        onPaid={() => loadData()} onClose={() => { setCieloPix(null); setSelectedTable(null); loadData(); }} />

      {/* ========== MODAL: Cancelar ========== */}
      <Dialog open={showCancel} onOpenChange={setShowCancel}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle><XCircle className="inline mr-2 text-red-500" size={20} />Cancelar Pedido</DialogTitle></DialogHeader>
          <div className="py-4">
            <Label>Motivo do cancelamento *</Label>
            <Input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Informe o motivo..." className="mt-1" data-testid="cancel-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancel(false)}>Voltar</Button>
            <Button onClick={handleCancelOrder} className="bg-red-600 hover:bg-red-700" disabled={!cancelReason.trim()} data-testid="confirm-cancel">
              Confirmar Cancelamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== MODAL: Desconto ========== */}
      <Dialog open={showDiscount} onOpenChange={setShowDiscount}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle><Percent className="inline mr-2 text-[#FF6B6B]" size={20} />Aplicar Desconto</DialogTitle></DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Tipo</Label>
              <Select value={discountType} onValueChange={setDiscountType}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PERCENT">Percentual (%)</SelectItem>
                  <SelectItem value="VALUE">Valor Fixo (R$)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Valor</Label>
              <Input type="number" step="0.01" value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                placeholder={discountType === 'PERCENT' ? 'Ex: 10 (%)' : 'Ex: 5.00 (R$)'}
                className="mt-1" data-testid="discount-value-input" />
            </div>
            {discountValue && orderSubtotal > 0 && (
              <p className="text-sm text-slate-600">
                Desconto: {formatCurrency(
                  discountType === 'PERCENT'
                    ? orderSubtotal * (parseFloat(discountValue) / 100)
                    : Math.min(parseFloat(discountValue), orderSubtotal)
                )}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiscount(false)}>Cancelar</Button>
            <Button onClick={handleApplyDiscount} className="bg-[#FF6B6B] hover:bg-[#FF5252]" disabled={!discountValue} data-testid="confirm-discount">
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== MODAL: Dividir Conta ========== */}
      <Dialog open={showSplit} onOpenChange={setShowSplit}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle><Split className="inline mr-2 text-[#FF6B6B]" size={20} />Dividir Conta</DialogTitle></DialogHeader>
          <div className="py-4 space-y-4">
            <div className="text-center">
              <p className="text-sm text-slate-500">Total da conta</p>
              <p className="text-2xl font-bold text-[#FF6B6B]">{formatCurrency(orderTotal)}</p>
            </div>
            <div>
              <Label>Dividir em quantas partes?</Label>
              <div className="flex items-center gap-3 mt-2 justify-center">
                <Button variant="outline" size="icon" onClick={() => setSplitParts(Math.max(2, splitParts - 1))}><Minus size={16} /></Button>
                <span className="text-3xl font-bold w-16 text-center">{splitParts}</span>
                <Button variant="outline" size="icon" onClick={() => setSplitParts(splitParts + 1)}><Plus size={16} /></Button>
              </div>
              <p className="text-center text-sm text-slate-600 mt-2">
                Cada parte: <strong>{formatCurrency(orderTotal / splitParts)}</strong>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSplit(false)}>Cancelar</Button>
            <Button onClick={handleSplitBill} className="bg-[#FF6B6B] hover:bg-[#FF5252]" data-testid="confirm-split">
              Dividir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== MODAL: Observacao do Item ========== */}
      <Dialog open={!!showItemNotes} onOpenChange={() => setShowItemNotes(null)}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle><MessageSquare className="inline mr-2 text-[#FF6B6B]" size={20} />Adicionar com observacao</DialogTitle></DialogHeader>
          <div className="py-4">
            <p className="font-medium mb-2">{showItemNotes?.name} - {showItemNotes && formatCurrency(showItemNotes.price)}</p>
            <Label>Observacao</Label>
            <Input value={itemNotes} onChange={(e) => setItemNotes(e.target.value)}
              placeholder="Ex: sem cebola, bem passado..."
              className="mt-1" data-testid="item-notes-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemNotes(null)}>Cancelar</Button>
            <Button onClick={() => { handleAddItem(showItemNotes); setShowItemNotes(null); }}
              className="bg-[#FF6B6B] hover:bg-[#FF5252]" data-testid="confirm-add-with-notes">
              Adicionar com Obs
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
